import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireAuth } from '@/app/RequireAuth';
import { AuditLogsPage } from '@/features/auditLogs/AuditLogsPage';
import { AccountPage } from '@/features/auth/AccountPage';
import { InvitationAcceptPage } from '@/features/auth/InvitationAcceptPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { PasswordResetConfirmPage } from '@/features/auth/PasswordResetConfirmPage';
import { PasswordResetRequestPage } from '@/features/auth/PasswordResetRequestPage';
import { StaffLoginPage } from '@/features/auth/StaffLoginPage';
import { TotpPage } from '@/features/auth/TotpPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { EventCreatePage } from '@/features/events/EventCreatePage';
import { OneOnOneCsvImportPage } from '@/features/events/OneOnOneCsvImportPage';
import { SuccessMeetingCsvImportPage } from '@/features/events/SuccessMeetingCsvImportPage';
import { EventDetailPage } from '@/features/events/EventDetailPage';
import { EventEvaluationPage } from '@/features/events/EventEvaluationPage';
import { EventListPage } from '@/features/events/EventListPage';
import { EventRecurringCreatePage } from '@/features/events/EventRecurringCreatePage';
import { EventOpsFlowsPage } from '@/features/help/EventOpsFlowsPage';
import { HelpIndexPage } from '@/features/help/HelpIndexPage';
import { HelpTopicPage } from '@/features/help/HelpTopicPage';
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage';
import { CohortsPage } from '@/features/masters/CohortsPage';
import { CoursesPage } from '@/features/masters/CoursesPage';
import { MastersHomePage } from '@/features/masters/MastersHomePage';
import { StaffInvitePage } from '@/features/staff/StaffInvitePage';
import { StaffMeetingsPage } from '@/features/staff/StaffMeetingsPage';
import { StaffPage } from '@/features/staff/StaffPage';
import { StaffProfileEditPage } from '@/features/staff/StaffProfileEditPage';
import { EventTypesPage } from '@/features/masters/EventTypesPage';
import { NgRulesPage } from '@/features/masters/NgRulesPage';
import { MemberCreatePage } from '@/features/members/MemberCreatePage';
import { MemberCsvImportPage } from '@/features/members/MemberCsvImportPage';
import { MemberDetailPage } from '@/features/members/MemberDetailPage';
import { MemberEditPage } from '@/features/members/MemberEditPage';
import { MemberEventResultPage } from '@/features/members/MemberEventResultPage';
import { MemberEventsPage } from '@/features/members/MemberEventsPage';
import { MemberPresidentSurveyPage } from '@/features/members/MemberPresidentSurveyPage';
import { MemberIntegrationsEditPage } from '@/features/members/MemberIntegrationsEditPage';
import { MemberListPage } from '@/features/members/MemberListPage';
import { ProfilePage } from '@/features/members/ProfilePage';
import { PortfolioPage } from '@/features/portfolio/PortfolioPage';
import { InboxItemDetailPage } from '@/features/messages/InboxItemDetailPage';
import { InboxPage } from '@/features/messages/InboxPage';
import { ThreadCreatePage } from '@/features/messages/ThreadCreatePage';
import { ThreadDetailPage } from '@/features/messages/ThreadDetailPage';
import { ThreadListPage } from '@/features/messages/ThreadListPage';
import { NotificationPresetsPage } from '@/features/notifications/NotificationPresetsPage';
import { NotificationPresetCreatePage } from '@/features/notifications/NotificationPresetCreatePage';
import { NotificationPresetEditPage } from '@/features/notifications/NotificationPresetEditPage';
import { NotificationTemplatesPage } from '@/features/notifications/NotificationTemplatesPage';
import { NotificationSchedulesPage } from '@/features/notifications/NotificationSchedulesPage';
import { NotificationsPage } from '@/features/notifications/NotificationsPage';
import { OfficeAccountInvitePage } from '@/features/officeAccounts/OfficeAccountInvitePage';
import { OfficeAccountsPage } from '@/features/officeAccounts/OfficeAccountsPage';
import { PresidentCsvImportPage } from '@/features/presidentSurveys/PresidentCsvImportPage';
import { PresidentExpectationsPage } from '@/features/presidentSurveys/PresidentExpectationsPage';
import { NgRuleRequestPage } from '@/features/seating/NgRuleRequestPage';
import { ScoreManagementPage } from '@/features/scores/ScoreManagementPage';
import { ScoreRankingPage } from '@/features/scores/ScoreRankingPage';
import { SuperAdminJobsPage } from '@/features/superadmin/SuperAdminJobsPage';
import { SuperAdminOfficeAccountsPage } from '@/features/superadmin/SuperAdminOfficeAccountsPage';
import { AvailabilityPage } from '@/features/availability/AvailabilityPage';
import { SurveyTemplatesPage } from '@/features/surveys/SurveyTemplatesPage';

/**
 * ルーティング。
 * 認証ガードは導線の制御であって認可ではない。**認可は API 側**（指示書 §0-6）。
 * 未実装のエリアはプレースホルダを置き、担当マイルストーンで差し替える。
 */
export function App(): ReactElement {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/staff/login" element={<StaffLoginPage />} />
      <Route path="/password/reset-request" element={<PasswordResetRequestPage />} />
      <Route path="/password/reset" element={<PasswordResetConfirmPage />} />
      <Route path="/totp" element={<TotpPage />} />
      <Route path="/invitations/accept" element={<InvitationAcceptPage />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <DashboardPage />
          </RequireAuth>
        }
      />

      {/* 会員（指示書 §12 M3） */}
      <Route
        path="/members"
        element={
          <RequireAuth>
            <MemberListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/members/new"
        element={
          <RequireAuth>
            <MemberCreatePage />
          </RequireAuth>
        }
      />
      <Route
        path="/members/import"
        element={
          <RequireAuth>
            <MemberCsvImportPage />
          </RequireAuth>
        }
      />
      <Route
        path="/members/:memberId"
        element={
          <RequireAuth>
            <MemberDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/members/:memberId/edit"
        element={
          <RequireAuth>
            <MemberEditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/members/:memberId/integrations"
        element={
          <RequireAuth>
            <MemberIntegrationsEditPage />
          </RequireAuth>
        }
      />
      {/* 社長アンケートの代理入力（D-315。会員詳細のセクションからは辿りにくかった） */}
      <Route
        path="/members/:memberId/president-survey"
        element={
          <RequireAuth>
            <MemberPresidentSurveyPage />
          </RequireAuth>
        }
      />
      {/* 参加イベント一覧（一覧だけの独立ページ / D-149） */}
      <Route
        path="/members/:memberId/events"
        element={
          <RequireAuth>
            <MemberEventsPage />
          </RequireAuth>
        }
      />
      {/* その受講生の、そのイベントでの結果（D-150） */}
      <Route
        path="/members/:memberId/events/:eventId"
        element={
          <RequireAuth>
            <MemberEventResultPage />
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        }
      />
      {/* 年間ポートフォリオ（D-113）。本人用と、会員を選んで見る用 */}
      <Route
        path="/portfolio"
        element={
          <RequireAuth>
            <PortfolioPage />
          </RequireAuth>
        }
      />
      <Route
        path="/members/:memberId/portfolio"
        element={
          <RequireAuth>
            <PortfolioPage />
          </RequireAuth>
        }
      />

      {/* イベント + 出欠 + 欠席申請（指示書 §12 M4） */}
      <Route
        path="/events"
        element={
          <RequireAuth>
            <EventListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/new"
        element={
          <RequireAuth>
            <EventCreatePage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/recurring/new"
        element={
          <RequireAuth>
            <EventRecurringCreatePage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/oneonone-import"
        element={
          <RequireAuth>
            <OneOnOneCsvImportPage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/success-meeting-import"
        element={
          <RequireAuth>
            <SuccessMeetingCsvImportPage />
          </RequireAuth>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <RequireAuth>
            <EventDetailPage />
          </RequireAuth>
        }
      />
      {/* 優秀者選定・態度フィードバック（理事のみ / implementation-spec §6.2・§12 M9 / DECISIONS D-034） */}
      <Route
        path="/events/:eventId/evaluation"
        element={
          <RequireAuth>
            <EventEvaluationPage />
          </RequireAuth>
        }
      />

      {/* 採点（受講生・事務局 / implementation-spec §10・§12 M9 / DECISIONS D-035〜D-037） */}
      <Route
        path="/scores"
        element={
          <RequireAuth>
            <ScoreRankingPage />
          </RequireAuth>
        }
      />
      <Route
        path="/scores/manage"
        element={
          <RequireAuth>
            <ScoreManagementPage />
          </RequireAuth>
        }
      />

      {/* 理事・講師／サクセス担当・事務局アカウント・業務マスター（指示書 §12 M3・M4）。
          招待は各一覧画面に統合し、独立した /invitations 画面は廃止した（D-140） */}
      <Route
        path="/directors"
        element={
          <RequireAuth>
            <StaffPage role="director" />
          </RequireAuth>
        }
      />
      <Route
        path="/directors/new"
        element={
          <RequireAuth>
            <StaffInvitePage role="director" />
          </RequireAuth>
        }
      />
      <Route
        path="/directors/:userId/edit"
        element={
          <RequireAuth>
            <StaffProfileEditPage role="director" />
          </RequireAuth>
        }
      />
      <Route
        path="/directors/:userId/meetings"
        element={
          <RequireAuth>
            <StaffMeetingsPage role="director" />
          </RequireAuth>
        }
      />
      <Route
        path="/success-staff"
        element={
          <RequireAuth>
            <StaffPage role="success" />
          </RequireAuth>
        }
      />
      <Route
        path="/success-staff/new"
        element={
          <RequireAuth>
            <StaffInvitePage role="success" />
          </RequireAuth>
        }
      />
      <Route
        path="/success-staff/:userId/edit"
        element={
          <RequireAuth>
            <StaffProfileEditPage role="success" />
          </RequireAuth>
        }
      />
      <Route
        path="/success-staff/:userId/meetings"
        element={
          <RequireAuth>
            <StaffMeetingsPage role="success" />
          </RequireAuth>
        }
      />
      <Route
        path="/office-accounts"
        element={
          <RequireAuth>
            <OfficeAccountsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/office-accounts/new"
        element={
          <RequireAuth>
            <OfficeAccountInvitePage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters"
        element={
          <RequireAuth>
            <MastersHomePage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/cohorts"
        element={
          <RequireAuth>
            <CohortsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/courses"
        element={
          <RequireAuth>
            <CoursesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/event-types"
        element={
          <RequireAuth>
            <EventTypesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/survey-templates"
        element={
          <RequireAuth>
            <SurveyTemplatesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/notification-presets"
        element={
          <RequireAuth>
            <NotificationPresetsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/notification-presets/new"
        element={
          <RequireAuth>
            <NotificationPresetCreatePage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/notification-presets/:presetId/edit"
        element={
          <RequireAuth>
            <NotificationPresetEditPage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/notification-templates"
        element={
          <RequireAuth>
            <NotificationTemplatesPage />
          </RequireAuth>
        }
      />
      <Route
        path="/masters/ng-rules"
        element={
          <RequireAuth>
            <NgRulesPage />
          </RequireAuth>
        }
      />

      {/* 席次NG申請（サクセス / implementation-spec §9・§12 M9） */}
      <Route
        path="/ng-rules/new"
        element={
          <RequireAuth>
            <NgRuleRequestPage />
          </RequireAuth>
        }
      />

      {/* 通知（指示書 §10: 受講生の通知一覧は必須 / §12 M6） */}
      <Route
        path="/notifications"
        element={
          <RequireAuth>
            <NotificationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/notifications/schedules"
        element={
          <RequireAuth>
            <NotificationSchedulesPage />
          </RequireAuth>
        }
      />

      {/* 社長アンケート（指示書 §12 M7） */}
      <Route
        path="/president"
        element={
          <RequireAuth>
            <PresidentExpectationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/surveys/president/import"
        element={
          <RequireAuth>
            <PresidentCsvImportPage />
          </RequireAuth>
        }
      />
      {/* /masters/president-surveys（社長アンケート設定）は D-146 で廃止
          （設問マスタをやめ固定フィールドにしたため、設定する画面が不要になった） */}

      {/* /surveys（課題・アンケートの独立画面）は D-157 で廃止。
          案内文だけのプレースホルダーで、回答・確認・集計はすべて各イベント詳細の
          「課題」タブ（事前課題・アンケート／事後アンケート）に実装済みだったため
          （ユーザー指摘「一覧見れてもあまり必要性を感じない」） */}

      {/* メッセージ + 共有受信箱（指示書 §12 M8） */}
      <Route
        path="/messages"
        element={
          <RequireAuth>
            <ThreadListPage />
          </RequireAuth>
        }
      />
      <Route
        path="/messages/new"
        element={
          <RequireAuth>
            <ThreadCreatePage />
          </RequireAuth>
        }
      />
      <Route
        path="/messages/:threadId"
        element={
          <RequireAuth>
            <ThreadDetailPage />
          </RequireAuth>
        }
      />
      <Route
        path="/inbox"
        element={
          <RequireAuth>
            <InboxPage />
          </RequireAuth>
        }
      />
      <Route
        path="/inbox/:inboxItemId"
        element={
          <RequireAuth>
            <InboxItemDetailPage />
          </RequireAuth>
        }
      />

      {/* 旧・枠予約方式（/scheduling・/success-meeting）と議事録の独立ページ
          （/success-records）は撤去した（D-182）。面談は事務局が個別作成し、
          面談記録はサクセス面談イベントの「面談記録」タブに書く */}
      <Route
        path="/availability"
        element={
          <RequireAuth>
            <AvailabilityPage />
          </RequireAuth>
        }
      />
      {/* フォローアップ画面は一旦導線を外す（D-300）。API・DBは残置 */}

      {/* 外部連携（指示書 §12 M11 / DECISIONS D-043）。
          データ移行画面は 2026-08-18 の先方回答で撤去（D-104）。過去データは既存CSV取込で投入する */}
      <Route
        path="/integrations"
        element={
          <RequireAuth>
            <IntegrationsPage />
          </RequireAuth>
        }
      />

      {/* 監査ログ + スーパー管理者（指示書 §12 M11 / DECISIONS D-044・D-046） */}
      <Route
        path="/audit-logs"
        element={
          <RequireAuth>
            <AuditLogsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/superadmin/office-admins"
        element={
          <RequireAuth>
            <SuperAdminOfficeAccountsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/superadmin/integrations"
        element={
          <RequireAuth>
            <IntegrationsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/superadmin/jobs"
        element={
          <RequireAuth>
            <SuperAdminJobsPage />
          </RequireAuth>
        }
      />

      <Route
        path="/account"
        element={
          <RequireAuth>
            <AccountPage />
          </RequireAuth>
        }
      />

      {/* 操作マニュアル（全ロール共通・利用者フィードバックで追加）。画面ごとに独立したページ */}
      <Route
        path="/help"
        element={
          <RequireAuth>
            <HelpIndexPage />
          </RequireAuth>
        }
      />
      {/* 事務局専用のイベント業務フロー（静的HTMLにしない・D-282） */}
      <Route
        path="/help/event-ops-flows"
        element={
          <RequireAuth>
            <EventOpsFlowsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/help/:topicId"
        element={
          <RequireAuth>
            <HelpTopicPage />
          </RequireAuth>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
